import { useEffect, useState } from "react";

import type { UsersOrderData } from "@/types";
import { Button } from "@/components/ui/button";
import { updateOrderById } from "@/utils/api";
import { useStateContext } from "@/context/ContextProvider";

interface OrderStatusButtonProps {
	currentOrder: UsersOrderData;
	setCurrentOrder: React.Dispatch<
		React.SetStateAction<UsersOrderData | undefined>
	>;
}

const OrderStatusButton = ({
	currentOrder,
	setCurrentOrder,
}: OrderStatusButtonProps) => {
	const { loggedInUserToken } = useStateContext()!;
	const [nextStatus, setNextStatus] = useState<string | null>(null);
	const [isUpdating, setIsUpdating] = useState(false);

	useEffect(() => {
		determineNextStatus();
	}, [currentOrder]);

	const determineNextStatus = () => {
		if (
			currentOrder.confirmed &&
			!currentOrder.collected &&
			!currentOrder.cancelled
		) {
			setNextStatus("collected");
		} else if (
			currentOrder.collected &&
			!currentOrder.delivered &&
			!currentOrder.cancelled
		) {
			setNextStatus("delivered");
		} else {
			setNextStatus(null);
		}
	};

	const handleStatusUpdate = async () => {
		if (nextStatus && !isUpdating) {
			setIsUpdating(true);
			let fieldToUpdate: string;
			switch (nextStatus) {
				case "collected":
					fieldToUpdate = "collected";
					break;
				case "delivered":
					fieldToUpdate = "delivered";
					break;
				default:
					fieldToUpdate = "";
			}

			// Optimistic update
			setCurrentOrder((prevOrder: UsersOrderData | undefined) => {
				if (!prevOrder) {
					return prevOrder;
				}
				return {
					...prevOrder,
					[fieldToUpdate]: true,
				};
			});

			try {
				const updatedOrderStatus = await updateOrderById(
					currentOrder.id,
					{
						[fieldToUpdate]: true,
					},
					loggedInUserToken,
				);

				if (!updatedOrderStatus) {
					// Revert optimistic update if API call fails
					setCurrentOrder((prevOrder: UsersOrderData | undefined) => {
						if (!prevOrder) {
							return prevOrder;
						}
						return {
							...prevOrder,
							[fieldToUpdate]: false,
						};
					});
				}
			} catch (error) {
				console.error("Error updating order status:", error);
				// Revert optimistic update on error
				setCurrentOrder((prevOrder: UsersOrderData | undefined) => {
					if (!prevOrder) {
						return prevOrder;
					}
					return {
						...prevOrder,
						[fieldToUpdate]: false,
					};
				});
			} finally {
				setIsUpdating(false);
				setNextStatus(null);
			}
		}
	};

	return (
		<Button
			onClick={handleStatusUpdate}
			disabled={!nextStatus || isUpdating}
			className="btn-primary bg-secondary"
		>
			{isUpdating
				? "Updating..."
				: nextStatus
					? `Mark as ${nextStatus}`
					: "No further action"}
		</Button>
	);
};

export default OrderStatusButton;
